import type * as React from "react";
import { type ReactNode } from "react";
import CustomNumberFormat from "../CustomNumberFormat";
import { Icon } from "../icon/Icon";
import { IconType } from "../icon/IconType";
import classNames from "classnames";

import { MAX_QUANTITY_LENGTH } from "../../../utils/constants";
import { useTranslation } from "react-i18next";
import { Box, IconButton, InputAdornment, InputLabel, styled } from "~/common/mui";
import "./QuantityInput.less";

interface UseQuantityInputProps {
  value?: number | null;
  step: number;
  onValueChange?: (newValue: number) => void;
  hideButtons?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  persistentMinValue?: boolean;
}

function useQuantityInput({
  value,
  disabled,
  step,
  min,
  max,
  onValueChange,
  persistentMinValue,
}: UseQuantityInputProps) {
  const onLessClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (disabled) {
      return;
    }

    const val = value ?? 0;
    const remainder = val % step;

    let newVal = val - (remainder ? remainder : step);
    newVal = clamp(newVal, { min, max });

    onValueChange?.(newVal);
  };

  const onMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (disabled) {
      return;
    }

    const val = value ?? 0;
    const remainder = val % step;

    let newVal = val + (step - remainder);
    newVal = clamp(newVal, { min, max });

    onValueChange?.(newVal);
  };

  const onInputChange = (newVal: number | undefined) => {
    if (newVal !== undefined && !isNaN(newVal)) {
      onValueChange?.(newVal);
    } else if (persistentMinValue && min !== undefined) {
      onValueChange?.(min);
    }
  };

  return {
    onInputChange,
    onLessClick,
    onMoreClick,
    lessDisabled: disabled || (min !== undefined && value !== undefined && value !== null && value <= min),
    moreDisabled: disabled || (max !== undefined && value !== undefined && value !== null && value >= max),
    inputDisabled: disabled,
  };
}

const clamp = (
  val: number,
  { min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY }: { min?: number; max?: number },
) => Math.max(min, Math.min(max, val));

export interface QuantityInputProps extends UseQuantityInputProps {
  id?: string;
  onBlur?: () => void;
  className?: string;
  error?: boolean;
  label?: ReactNode;
  helperLabel?: ReactNode;
  "data-test-id"?: string;
  startAdornment?: ReactNode;
  variant?: "primary" | "compact";
  persistentMinValue?: boolean;
  fullWidth?: boolean;
}

const StyledInput = styled(CustomNumberFormat)({
  "& .MuiInputBase-root": {
    paddingInline: 0,
  },
  "& .MuiInputBase-input": {
    textAlign: "center",
  },
});

const IncrementButton = styled(IconButton)({
  "&.MuiIconButton-root": {
    padding: "12px",
    borderRadius: "4px",
  },
});

export const QuantityInput = ({
  className,
  "data-test-id": testId,
  id,
  error,
  label,
  helperLabel,
  hideButtons,
  startAdornment,
  variant,
  ...rest
}: QuantityInputProps) => {
  const { t } = useTranslation();

  const { onInputChange, inputDisabled, onLessClick, lessDisabled, onMoreClick, moreDisabled } = useQuantityInput(rest);

  const onBlur = () => {
    if (rest.value !== undefined && rest.value !== null) {
      const clampedVal = clamp(rest.value, rest);
      if (clampedVal !== rest.value) {
        rest.onValueChange?.(clampedVal);
      }
    }

    rest.onBlur?.();
  };

  if (variant === "compact") {
    return (
      <Box className={className}>
        <InputLabel shrink>{label ?? t("input.quantityPieces")}</InputLabel>
        <StyledInput
          value={rest.value}
          id={id}
          handleState={(e) => onInputChange(e.floatValue)}
          disabled={inputDisabled}
          error={error}
          allowedLength={MAX_QUANTITY_LENGTH}
          helperText={helperLabel}
          data-test-id={testId}
          onBlur={onBlur}
          slotProps={{
            input: {
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <IncrementButton
                      disabled={lessDisabled}
                      onBlur={onBlur}
                      onClick={onLessClick}
                      variant={"contained"}
                      color={"secondary"}
                    >
                      <Icon type={IconType.Minus} size={16} />
                    </IncrementButton>
                  </InputAdornment>
                  {startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  <InputAdornment position="end">
                    <IncrementButton
                      disabled={moreDisabled}
                      onBlur={onBlur}
                      onClick={onMoreClick}
                      variant={"contained"}
                      color={"secondary"}
                    >
                      <Icon type={IconType.Plus} />
                    </IncrementButton>
                  </InputAdornment>
                </>
              ),
            },
          }}
          size={"small"}
        />
      </Box>
    );
  }

  const cls = classNames("scha__qty-input", className);

  return (
    <div className={cls}>
      <CustomNumberFormat
        label={label ?? t("input.quantity")}
        value={rest.value}
        id={id}
        handleState={(e) => onInputChange(e.floatValue)}
        disabled={inputDisabled}
        error={error}
        allowedLength={MAX_QUANTITY_LENGTH}
        helperText={helperLabel}
        data-test-id={testId}
        onBlur={onBlur}
        slotProps={{
          input: {
            startAdornment,
          },
        }}
      />
      {!hideButtons && (
        <div className={"scha__qty-input__btn-container"}>
          <button type="button" disabled={lessDisabled} onClick={onLessClick} onBlur={onBlur}>
            <Icon type={IconType.Minus} />
          </button>
          <button type="button" disabled={moreDisabled} onClick={onMoreClick} onBlur={onBlur}>
            <Icon type={IconType.Plus} />
          </button>
        </div>
      )}
    </div>
  );
};
